import React from 'react';

import { CustomInput } from '../../../components';
import {
  BookingErrors,
  BookingFormValues,
  needsOwnWaybill,
  PartyForm,
} from './bookingForm';
import type { NormalizedQuote } from '../../../apis/lanePlanner';
import styles from './styles.module.css';

interface FieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  placeholder?: string;
  maxLength?: number;
  optional?: boolean;
}

const Field: React.FC<FieldProps> = ({
  label,
  value,
  onChange,
  error,
  placeholder,
  maxLength,
  optional,
}) => (
  <label className={styles.bookingField}>
    <span className={styles.fieldLabel}>{label}</span>
    <CustomInput
      value={value}
      maxLength={maxLength}
      placeholder={placeholder ?? (optional ? 'Optional' : undefined)}
      status={error ? 'error' : undefined}
      aria-label={label}
      onChange={(e) => onChange(e.target.value)}
    />
    {error && <span className={styles.fieldError}>{error}</span>}
  </label>
);

interface PartyCardProps {
  title: 'Shipper' | 'Consignee';
  addressLabel: string;
  party: PartyForm;
  errors: BookingErrors;
  errorKey: 'shipper' | 'consignee';
  onChange: (party: PartyForm) => void;
}

const PartyCard: React.FC<PartyCardProps> = ({
  title,
  addressLabel,
  party,
  errors,
  errorKey,
  onChange,
}) => (
  <section className={styles.bookingCard} aria-label={title}>
    <h4 className={styles.bookingCardTitle}>{title}</h4>
    <div className={styles.bookingGrid}>
      <Field
        label={`${title} company`}
        value={party.company}
        placeholder='Company name'
        maxLength={200}
        error={errors[`${errorKey}.company`]}
        onChange={(company) => onChange({ ...party, company })}
      />
      <Field
        label={`${title} contact`}
        value={party.contact}
        placeholder='Name, email or phone'
        maxLength={200}
        onChange={(contact) => onChange({ ...party, contact })}
      />
    </div>
    <Field
      label={addressLabel}
      value={party.address}
      maxLength={300}
      error={errors[`${errorKey}.address`]}
      onChange={(address) => onChange({ ...party, address })}
    />
    <Field
      label={`${title} facility hours`}
      value={party.facilityHours}
      placeholder='e.g. 8:00 AM – 4:00 PM, dock 4'
      maxLength={200}
      onChange={(facilityHours) => onChange({ ...party, facilityHours })}
    />
  </section>
);

interface BookingFormStepProps {
  quote: NormalizedQuote;
  values: BookingFormValues;
  errors: BookingErrors;
  onChange: (values: BookingFormValues) => void;
  onBack: () => void;
  onContinue: () => void;
}

const BookingFormStep: React.FC<BookingFormStepProps> = ({
  quote,
  values,
  errors,
  onChange,
  onBack,
  onContinue,
}) => {
  const set = <K extends keyof BookingFormValues>(
    key: K,
    value: BookingFormValues[K]
  ): void => onChange({ ...values, [key]: value });

  return (
    <div>
      <PartyCard
        title='Shipper'
        addressLabel='Pickup address'
        party={values.shipper}
        errors={errors}
        errorKey='shipper'
        onChange={(party) => set('shipper', party)}
      />
      <PartyCard
        title='Consignee'
        addressLabel='Delivery address'
        party={values.consignee}
        errors={errors}
        errorKey='consignee'
        onChange={(party) => set('consignee', party)}
      />
      <section className={styles.bookingCard} aria-label='Commodity'>
        <h4 className={styles.bookingCardTitle}>Commodity</h4>
        <div className={styles.bookingGrid}>
          <Field
            label='Description'
            value={values.description}
            placeholder='e.g. Biologics — clinical supply'
            maxLength={300}
            error={errors.description}
            onChange={(v) => set('description', v)}
          />
          <Field
            label='PO / reference'
            value={values.poReference}
            optional
            maxLength={100}
            onChange={(v) => set('poReference', v)}
          />
        </div>
        <Field
          label='Pickup number'
          value={values.pickupNumber}
          optional
          maxLength={100}
          onChange={(v) => set('pickupNumber', v)}
        />
        <Field
          label='Handling notes'
          value={values.handlingNotes}
          placeholder='Optional — e.g. deliver to dock 4, call ahead'
          maxLength={500}
          onChange={(v) => set('handlingNotes', v)}
        />
      </section>
      {needsOwnWaybill(quote) && (
        <section className={styles.bookingCard} aria-label='Air waybill'>
          <h4 className={styles.bookingCardTitle}>Air waybill</h4>
          <div className={styles.partialNote}>
            This airline does not assign a waybill. Enter one from your own
            stock.
          </div>
          <div className={styles.bookingGrid}>
            <Field
              label='Waybill prefix'
              value={values.waybillPrefix}
              placeholder='3 digits'
              maxLength={3}
              error={errors.waybillPrefix}
              onChange={(v) => set('waybillPrefix', v.replace(/\D/g, ''))}
            />
            <Field
              label='Waybill number'
              value={values.waybillNumber}
              placeholder='8 digits'
              maxLength={8}
              error={errors.waybillNumber}
              onChange={(v) => set('waybillNumber', v.replace(/\D/g, ''))}
            />
          </div>
        </section>
      )}
      <div className={styles.bookingActions}>
        <button
          type='button'
          className={styles.secondaryAction}
          onClick={onBack}
        >
          Back
        </button>
        <button
          type='button'
          className={styles.primaryAction}
          onClick={onContinue}
        >
          Continue to review
        </button>
      </div>
    </div>
  );
};

export default BookingFormStep;
